import React, { useState, useEffect } from "react";
import RateBox from "../Rates/RateBox";
import Dropdown from "../../../Form/FormDropdown";
import CalculatorInputs from "../../basics/Inputs";
import { validateFormElements } from "../../../../utilities/formValidators";
import styled from "styled-components";
import { getPaymentCount } from "../../common/helpers";
import InputSlider from "../../basics/InputSlider";
import useLanguage from "../../../../utilities/useLanguage";
import { getTranslation } from "../../translations";

const CalculatorContainer = styled.div`

    @media(min-width:769px) and (max-width:990px){
        h3{
            font-size:25px !important;
        }
        .per-month{
            font-size:12px;
            line-height: 1rem;
        }

        .amount{
            font-size:29px;
        }
    }

`;
export interface ICalcProps {
    onRowClick: (object) => void;
    values;
    onChange;
    dropdownState;
    aprRates;
    labels;
    headline;
    maxLTVRates;
}

export default (props: ICalcProps) => {
    const { headline, aprRates, values, onRowClick, onChange, dropdownState, labels, maxLTVRates } = props;
    const [rates, setRates] = useState({ loaded: false, options: [], formValues: values });
    const [sliderValue, setSliderValue] = useState(0);
    const { lang } = useLanguage();

    useEffect(() => {
        if (aprRates.loaded) {
            calculateRateDetails(aprRates.data, values);
        }
    }, [values, aprRates.loaded, aprRates.data, sliderValue]);

    //intercept the data going out to the modal
    //and array.merge values and data for display in modal
    function setDetailedQuoteData(event, data) {
        data.LoanAmount = parseFloat(values.PurchasePrice.value) - parseFloat(values.DownPayment.value);
        onRowClick(data);
        onChange("LoanTerm", data.id);
    }

    function calculateRateDetails(rates, values) {
        let calculatedRates;
        if (values.LoanPurpose.value === "Purchase") {
            // if mrqt is used for "Purchase", use down payment and purchace price
            calculatedRates = calculateMonthlyPayments(rates, parseInt(values.DownPayment.value), parseInt(values.PurchasePrice.value), true);
        } else {
            // if mrqt is used for "Refinance", use refinance amount and current home value
            calculatedRates = calculateMonthlyPayments(rates, parseInt(values.RefinanceAmount.value), parseInt(values.CurrentHomeValue.value), false);
        }

        setRates({
            loaded: true,
            options: calculatedRates,
            formValues: values
        });
    }

    function calculateMonthlyPayment(rateObject, downPayment /* refi amount */, purchasePrice /* current home value */, isPurchase) {
        const monthsInYear = 12;
        const maxLTV = maxLTVRates.data[rateObject.id] ?? maxLTVRates.data.default;
        rateObject.error = validateFormElements(
            values.LoanPurpose.value,
            parseInt(values.DownPayment.value),
            parseInt(values.RefinanceAmount.value),
            parseInt(values.PurchasePrice.value),
            parseInt(values.CurrentHomeValue.value),
            rateObject.id,
            values.FirstTimeLimit.value,
            values.DefaultLoanLimit.value,
            maxLTV,
            lang,
        );
        let loanValue = downPayment;

        if (isPurchase) {
            // adjust loan value if purchase
            loanValue = purchasePrice - downPayment;
        }

        rateObject.rate.disclosureInformation.modifiedApr = rateObject.rate.disclosureInformation.apr + sliderValue;
        rateObject.rate.modifiedInitialRate = rateObject.rate.initialRate + sliderValue;

        const paymentCount = getPaymentCount(rateObject.rate.disclosureInformation);
        const rateDecimal = rateObject.rate.modifiedInitialRate / 100;
        const monthlyRate = rateDecimal / monthsInYear;
        const power = Math.pow(1 + monthlyRate, paymentCount);

        const monthlyPayment = loanValue * (monthlyRate * power / (power - 1));

        // Loan term is used for the detailed quote request and it needs paymentCount when it exists regardless if the loan is a balloon one
        const loanTermPaymentCount = rateObject.rate.disclosureInformation.paymentCount || rateObject.rate.disclosureInformation.termMonths;

        rateObject.loanTerm = loanTermPaymentCount / monthsInYear;
        rateObject.monthlyPayment = monthlyPayment;

        return rateObject;
    }

    function calculateMonthlyPayments(rates, downPayment, purchasePrice, isPurchase) {
        return rates.map(rate => {
            return calculateMonthlyPayment(rate, downPayment, purchasePrice, isPurchase);
        });
    }

    function handleDropdownChange(e) {
        const { name, value } = e.target;
        onChange(name, value);
    }

    function handleInputSliderChange(percentage) {
        setSliderValue(percentage);
    }

    function CalculatorDropdown({ id, className = "", disabled = false, selectClassName }) {
        return (
            <Dropdown
                id={id}
                placeholder={dropdownState[id].name}
                options={dropdownState[id].data}
                onChange={handleDropdownChange}
                defaultValue={values[id].value || null}
                className={className}
                selectClassName={selectClassName}
                disabled={disabled}
            />
        );
    }

    const calcProps = {
        labels,
        values,
        containerClassName: "col-6",
        onChange,
        inputClassName: "px-3 bg-gray-200"
    };

    return (
        <CalculatorContainer>
            <div className="row">
                <div className="col-12 px-0">
                    <h5 className="mb-4 ml-3 ml-md-0 text-black">{headline}</h5>
                </div>
            </div>
            {/* TODO: remove this currently until refinance/cash out is pulled from rate service
             <div className="mrqt-row">
                <CalculatorDropdown id="LoanPurpose" className="mrqt-col-12 px-0" selectClassName="bg-gray-200" />
            </div> */}
            <div className="row mb-3">
                <CalculatorInputs {...calcProps} />
            </div>
            <p className="font-weight-bold mb-1">{getTranslation(lang).customizeRate}</p>
            <div className="mrqt-row mb-3">
                <InputSlider onChange={handleInputSliderChange} />
            </div>
            <div className="row">
                <RateBox
                    rates={rates.options}
                    onClick={setDetailedQuoteData} />
            </div>
            <div className="row">
                <div className="col-12 pr-0 text-muted text-right">
                    <small>*{getTranslation(lang).onApprovedCredit}</small>
                </div>
            </div>
        </CalculatorContainer>
    );
};
